import type { AnalyticsEvent, AnalyticsEventName, AnalyticsEventProperties } from '../events';
import type { OneDSEventPayload } from '../report/eventSink';
import { emitTelemetryEvent, emitTelemetryEventNow } from '../report/eventSink';

import { forwardEventToCustomDataStore } from './customDataStoreSubscriber';
import { filterUnsafeProperties } from './eventPropertyCheck';

interface ReportUsageOptions {
  /**
   * The event's name as defined in the AnalyticsEventName type. This will be logged in the event's
   * 'EventName' column.
   */
  readonly eventName: AnalyticsEventName;
  /**
   * An optional set of custom properties to log for the event. These properties must be defined in
   * an interface in the telemetry types file and added as part of the AnalyticsEventProperties type
   * (see the README about the process for adding new custom properties). Each value passed here will
   * be logged in a column named with the key within "properties" in the yamjsevents table.
   */
  readonly eventProperties?: AnalyticsEventProperties;
}

const getAnalyticsEvent = (options: ReportUsageOptions): AnalyticsEvent => {
  const { eventName, eventProperties = {} } = options;

  return {
    type: 'Analytics',
    name: eventName,
    occurredAt: Date.now().toString(),
    properties: filterUnsafeProperties(eventName, eventProperties),
  };
};

const buildAnalyticsOneDSPayload = (event: AnalyticsEvent): OneDSEventPayload => ({
  baseType: event.type,
  name: event.name,
  data: event.properties,
});

export type ReportUsage = (options: ReportUsageOptions) => void;
/**
 * Reports existing legacy usage events. Define new usage events as protobuf events and report them
 * with `reportAnalyticsEvent`.
 */
export const reportUsage: ReportUsage = (options) => {
  const analyticsEvent = getAnalyticsEvent(options);
  forwardEventToCustomDataStore(analyticsEvent);
  void emitTelemetryEvent(analyticsEvent, buildAnalyticsOneDSPayload(analyticsEvent));
};

type ReportUsageNow = (options: ReportUsageOptions) => Promise<void>;
export const reportUsageNow: ReportUsageNow = async (options) => {
  const analyticsEvent = getAnalyticsEvent(options);
  await emitTelemetryEventNow(analyticsEvent, buildAnalyticsOneDSPayload(analyticsEvent));
};
